
import { Component, Vue, Ref } from 'vue-property-decorator'
import SelectStudyCategoryContentList from '@/components/templates/SelectStudyCategoryContentList.vue'
import ModalSelectSubject from '@/views/modal/ModalSelectSubject.vue'
import ModalSelectGrade from '@/views/modal/ModalSelectGrade.vue'
import ModalSelectEntranceStudyCategory from '@/views/modal/ModalSelectEntranceStudyCategory.vue'
import CurriculumUnitFilters from '@/utils/CurriculumUnitFilters'
import { ProgressFilter } from '@/const/ProgressFilter'
import { SubjectCode } from '@/const/SubjectCode'
import { GradeCode } from '@/const/GradeCode'
import { LearningProgress } from '@/const/LearningProgress'
import { StudyCategoryCurriculumUnit } from '@/models/CurriculumUnit'

/**
 * 入試直前トレーニング
 */
@Component({
  components: {
    SelectStudyCategoryContentList,
    ModalSelectSubject,
    ModalSelectGrade,
    ModalSelectEntranceStudyCategory,
  },
})
export default class SeminarStudy extends Vue {
  /** 学習教材選択ページレイアウト */
  @Ref()
  selectStudyCategoryContentList!: SelectStudyCategoryContentList

  /** 教科選択モーダル */
  @Ref()
  modalSelectSubject!: ModalSelectSubject

  /** 学年選択モーダル */
  @Ref()
  modalSelectGrade!: ModalSelectGrade

  /** 入試直前トレーニング教材選択モーダル */
  @Ref()
  modalSelectEntranceStudyCategory!: ModalSelectEntranceStudyCategory

  /** 選択中の教科コード */
  private currentSubjectCode: string = SubjectCode.LIST[0].code

  /** 選択中の学年コード */
  private currentGradeCode: string = GradeCode.LIST[0].code

  /** 選択中の学習教材コード */
  private currentStudyCategoryCode: string = ''

  /** 選択中の学習教材名 */
  private currentStudyCategoryName: string = ''

  /** 学習進捗選択ボタンの初期値 */
  private defaultProgressFilterCode: string = ProgressFilter.LIST[0].code

  /** 学習教材用単元情報のリスト */
  private studyCategoryCurriculumUnitList: StudyCategoryCurriculumUnit[] = []

  /** コイン画像のURL */
  private coinUrl = '/img/coin_dummy.png'

  /** 学習教材に紐づく単元の数 */
  private curriculumUnitCount = 0

  /**
   * 教材画像URL
   *
   * @returns 教材イメージのパス
   */
  private get textbookImagePath(): string | null {
    const studyCategory = this.studyCategoryList.find((studyCategory) => {
      return studyCategory.code === this.currentStudyCategoryCode
    })
    if (!studyCategory) {
      return null
    }

    return studyCategory.textbookImagePath
  }

  /**
   * 学習教材用単元情報のリストを学習進捗でフィルタリングする
   *
   * @returns フィルタリングされた単元教材用単元情報のリスト
   */
  private get filterStudyCategoryCurriculumUnitList(): StudyCategoryCurriculumUnit[] {
    return CurriculumUnitFilters.byProgressFilter(
      this.studyCategoryCurriculumUnitList,
      this.currentProgressFilterCode()
    )
  }

  /**
   * 教科選択モーダルのsubmitイベント発火時の処理
   *
   * @param code 選択中の教科コード
   */
  private eventSubmitSubject(code: string): void {
    this.currentSubjectCode = code
    this.currentStudyCategoryCode = ''
    this.getStudyCategoryData()
  }

  /**
   * 学年選択モーダルのsubmitイベント発火時の処理
   *
   * @param code 選択中の教科コード
   */
  private eventSubmitGrade(code: string): void {
    this.currentGradeCode = code
    this.currentStudyCategoryCode = ''
    this.getStudyCategoryData()
  }

  /**
   * 確認ボタン押下時のイベント
   *
   * @param code 学習教材コード
   */
  private eventSubmitStudyCategory(code: string): void {
    this.currentStudyCategoryCode = code
    this.getStudyCategoryData()
  }

  /**
   * 教科選択ボタン押下時のイベント
   */
  private onSelectSubjectButton(): void {
    this.modalSelectSubject.show(this.currentSubjectCode)
  }

  /**
   * 学年選択ボタン押下時のイベント
   */
  private onSelectGradeButton(): void {
    this.modalSelectGrade.show(this.currentGradeCode)
  }

  /**
   * 入試直前トレーニング教材選択ボタン押下時のイベント
   */
  private onSelectEntranceStudyCategoryButton(): void {
    this.modalSelectEntranceStudyCategory.show(this.currentStudyCategoryCode)
  }

  /**
   * 選択中の学習進捗コード
   *
   * @returns 学習進捗コード
   */
  private currentProgressFilterCode(): string {
    // DOM未処理によるundefinedブロック
    if (!this.selectStudyCategoryContentList) {
      return ''
    }
    return this.selectStudyCategoryContentList.currentProgressFilterCode()
  }

  /**
   * 学習教材用単元情報のリストを更新
   */
  private getStudyCategoryData(): void {
    this.$loading.start()
    if (!this.currentStudyCategoryCode) {
      // TODO api実装後修正
      this.currentStudyCategoryCode = '1'
    }
    this.getStudyCategoryCurriculumUnitList()
    this.getCurrentStudyCategoryName()
    this.$loading.complete()
  }

  /**
   * apiから学習教材用単元情報のリストを取得
   * TODO api実装時に修正
   */
  private getStudyCategoryCurriculumUnitList(): void {
    // 学習教材に紐づく単元の一覧を取得
    this.studyCategoryCurriculumUnitList = [
      { id: 1, name: '単元情報ダミー1', learningProgress: LearningProgress.NONE, viewNumber: '1' },
      { id: 2, name: '単元情報ダミー2', learningProgress: LearningProgress.EXAMPLE, viewNumber: '2' },
      { id: 3, name: '単元情報ダミー3', learningProgress: LearningProgress.LEVEL_A, viewNumber: '3' },
      { id: 4, name: '単元情報ダミー4', learningProgress: LearningProgress.LEVEL_B, viewNumber: '4' },
      { id: 5, name: '単元情報ダミー5', learningProgress: LearningProgress.LEVEL_C, viewNumber: '5' },
      { id: 6, name: '単元情報ダミー6', learningProgress: LearningProgress.COMPLETE, viewNumber: '6' },
      { id: 7, name: '単元情報ダミー7', learningProgress: LearningProgress.NONE, viewNumber: '7' },
    ]
    // 単元の数を取得
    this.curriculumUnitCount = this.studyCategoryCurriculumUnitList.length
  }

  /**
   * apiから選択中の学習教材名を取得
   * TODO api実装時修正
   */
  private getCurrentStudyCategoryName(): void {
    // api実装時はcurrentStudyCategoryCodeから直接引き当てる
    const list = [
      { code: '1', name: 'ダミー1' },
      { code: '2', name: 'ダミー2' },
      { code: '3', name: 'ダミー3' },
      { code: '4', name: 'ダミー4' },
      { code: '5', name: 'ダミー5' },
      {
        code: '6',
        name: 'ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6ダミー6',
      },
    ]
    const name = list.find((item) => item.code === this.currentStudyCategoryCode)?.name
    this.currentStudyCategoryName = name || ''
  }

  /**
   * 初期処理
   */
  private mounted(): void {
    // 学習教材のデータを取得
    this.getStudyCategoryData()
  }

  /** ダミーデータ */
  private studyCategoryList = [
    { id: 1, code: '1', name: 'ダミー1', textbookImagePath: '/img/dummy_study_category/textbook.png' },
    { id: 2, code: '2', name: 'ダミー2', textbookImagePath: null },
    { id: 3, code: '3', name: 'ダミー3', textbookImagePath: null },
    { id: 4, code: '4', name: 'ダミー4', textbookImagePath: null },
    { id: 5, code: '5', name: 'ダミー5', textbookImagePath: null },
    { id: 6, code: '6', name: 'ダミー6', textbookImagePath: null },
  ]
}
